import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { isNativeApp } from "@/lib/native";

/**
 * Bottom-anchored install-app banner.
 * Shows when the browser fires `beforeinstallprompt` (Chrome/Edge on desktop,
 * Android). iOS Safari doesn't fire that event — we detect iOS separately
 * and show a "Tap Share → Add to Home Screen" hint the first time only.
 *
 * Dismissal is remembered for 14 days via localStorage.
 */
const DISMISS_KEY = "pwa_install_dismissed_until";
const DISMISS_DAYS = 14;

const isIos = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const dismissedRecently = () => {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until > Date.now();
  } catch { return false; }
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Silent inside the native Capacitor shell — the user already installed it.
    if (isNativeApp() || isStandalone() || dismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari — no beforeinstallprompt event; show hint after a small delay
    // so it doesn't fight for attention during onboarding.
    if (isIos()) {
      const t = setTimeout(() => {
        if (!isStandalone() && !dismissedRecently()) {
          setShowIosHint(true);
          setVisible(true);
        }
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400_000));
    } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch { dismiss(); }
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 z-[70] mx-auto max-w-md rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-900 to-neutral-950 p-4 shadow-2xl backdrop-blur-md md:left-auto md:right-6 md:bottom-6"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-heat/20 text-heat">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 text-sm">
          <p className="font-heading text-white">Install Edge Training</p>
          {showIosHint ? (
            <p className="mt-1 text-xs leading-relaxed text-neutral-400">
              Tap the <span className="font-semibold text-white">Share</span> icon in Safari, then
              <span className="font-semibold text-white"> Add to Home Screen</span>.
            </p>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-neutral-400">
              Add it to your home screen for a full-screen experience and quick access to workouts.
            </p>
          )}
          {!showIosHint && deferred && (
            <button
              data-testid="pwa-install-btn"
              type="button"
              onClick={install}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-heat px-4 py-1.5 text-xs font-semibold text-white shadow-lg transition hover:bg-heat/90"
            >
              <Download className="h-3.5 w-3.5" /> Install
            </button>
          )}
        </div>
        <button
          data-testid="pwa-install-dismiss"
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="rounded-full p-1 text-neutral-400 transition hover:bg-white/5 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
